import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Button,
  Space,
  Row,
  Select,
  InputNumber,
  message,
  Skeleton,
} from "antd";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./invoiceform.css";
import TextArea from "antd/es/input/TextArea";
import {
  useCreateQuotationTemplateMutation,
  useUpdateQuotationTemplateMutation,
  useFetchCategoryQuery,
  useGetQuotationTemplateQuery,
} from "../../store/store";
import ItemTable from "../models/ItemTable";

const unit = [
  "SQR METER",
  "MILI METER",
  "INCH",
  "SQR FOOT",
  "RUNNING FOOT",
  "NUMBERS",
  "LUMPSUM",
  "APPROXIMATE",
];

const layout = {
  labelCol: { span: 5 },
  wrapperCol: { span: 19 },
};

const QuotationTemplateForm = ({ datas, id, show }) => {
  const [form] = Form.useForm();
  const [total_bam, setTotal_bam] = useState();
  const [total, setTotal] = useState();

  // Use a ref so onFinish always reads the latest ReactQuill value without
  // relying on closure staleness across re-renders.
  const termsRef = useRef("");
  const [termsValue, setTermsValue] = useState("");
  const setTerms = (val) => {
    termsRef.current = val;
    setTermsValue(val);
  };

  const { data: category } = useFetchCategoryQuery();
  const [createTemplate, createInfo] = useCreateQuotationTemplateMutation();
  const [updateTemplate, updateInfo] = useUpdateQuotationTemplateMutation();

  // Fetch full detail (includes terms_and_conditions + nested items) when editing.
  // The list query used by the parent only returns summary fields.
  const { data: fullDetail, isLoading: detailLoading } =
    useGetQuotationTemplateQuery(id, { skip: !id });

  const ultimate = () => {
    const currentItems = form.getFieldValue("items") || [];
    const data2 = currentItems.map((item) => {
      const length = item?.length || 1;
      const height = item?.height || 1;
      const width = item?.width || 1;
      const depth = item?.depth || 1;
      const running_foot = item?.running_foot || 1;
      const sqft = item?.sqft || 1;
      const quantity = item?.quantity || 1;
      const numbers = item?.numbers || 1;
      const rowTotal =
        parseFloat(item?.costing || 0) *
        length *
        height *
        width *
        depth *
        running_foot *
        numbers *
        quantity *
        sqft;
      return { ...item, total: rowTotal };
    });

    data2.forEach((it) => {
      const resolved = category?.find(
        (c) => parseInt(c.id) === parseInt(it.item_category)
      )?.category;
      if (resolved !== undefined) it.item_category = resolved;
    });

    const totalSum = data2.reduce(
      (acc, curr) => acc + parseFloat(curr.total || 0),
      0
    );
    setTotal(totalSum);
    form.setFieldsValue({ items: data2 });
  };

  useEffect(() => {
    if (total_bam !== undefined) {
      ultimate();
    }
  }, [total_bam]);

  const datafun = (selectedItems) => {
    const currentFormItems = form.getFieldValue("items") || [];

    if (currentFormItems.length) {
      const mergedData = currentFormItems.map((fi) => {
        const updated = selectedItems.find(
          (s) => s.id === (fi.item_id || fi.id)
        );
        return updated
          ? { ...fi, ...updated, item_id: updated.id, id: fi.id }
          : fi;
      });
      const existingItemIds = currentFormItems.map((fi) => fi.item_id || fi.id);
      const newItems = selectedItems
        .filter((s) => !existingItemIds.includes(s.id))
        .map((s) => ({ ...s, item_id: s.id, id: undefined }));
      form.setFieldsValue({ items: [...mergedData, ...newItems] });
    } else {
      form.setFieldsValue({
        items: selectedItems.map((s) => ({ ...s, item_id: s.id, id: undefined })),
      });
    }

    setTotal_bam(selectedItems);
  };

  // When editing: populate form from the DETAIL query (has all fields including
  // terms_and_conditions). When creating: reset everything.
  useEffect(() => {
    if (id && fullDetail) {
      form.setFieldsValue({
        title: fullDetail.title,
        description: fullDetail.description,
        special_note: fullDetail.special_note,
        remark: fullDetail.remark,
        items: fullDetail.items || [],
      });
      setTerms(fullDetail.terms_and_conditions || "");

      if (fullDetail.items?.length) {
        setTotal_bam(
          fullDetail.items.map((item) => ({
            ...item,
            id: item.item_id || item.id,
          }))
        );
      }
    } else if (!id) {
      form.resetFields();
      setTerms("");
      setTotal_bam(undefined);
      setTotal(undefined);
    }
  }, [fullDetail, id]);

  useEffect(() => {
    if (createInfo?.isSuccess || updateInfo?.isSuccess) {
      message.success(id ? "Template updated" : "Template created");
      show(false);
    }
    if (createInfo?.isError) message.error("Create failed");
    if (updateInfo?.isError) message.error("Update failed");
  }, [createInfo, updateInfo]);

  const onFinish = (values) => {
    // Use the ref so we always get the latest ReactQuill value even if React
    // hasn't flushed the termsValue state update yet.
    const payload = { ...values, terms_and_conditions: termsRef.current };
    if (id) {
      updateTemplate({ id, ...payload });
    } else {
      createTemplate(payload);
    }
  };

  if (id && detailLoading) {
    return <Skeleton active style={{ padding: "24px" }} />;
  }

  return (
    <div
      style={{
        display: "flex",
        width: "100%",
        justifyContent: "center",
        alignItems: "flex-start",
      }}
    >
      <div
        className="form-box"
        style={{ width: "100%", maxHeight: "78vh", overflowY: "auto" }}
      >
        <Form
          form={form}
          {...layout}
          onFinish={onFinish}
          onValuesChange={(changedValues, allValues) => {
            if ("items" in changedValues) {
              setTotal_bam(
                (allValues.items || []).map((item) => ({
                  ...item,
                  id: item.item_id || item.id,
                }))
              );
            }
          }}
          style={{ maxWidth: "100%" }}
        >
          {/* ── Template Details ── */}
          <div className="address">
            <h3 className="h3-title">Template Details</h3>

            <Form.Item
              label="Title"
              name="title"
              labelCol={5}
              rules={[{ required: true, message: "Title is required" }]}
            >
              <Input placeholder="Enter template title" />
            </Form.Item>

            <Form.Item label="Description" name="description" labelCol={5}>
              <TextArea rows={2} placeholder="Enter description" />
            </Form.Item>

            <Form.Item label="Special Note" name="special_note" labelCol={5}>
              <Input placeholder="Enter special note" />
            </Form.Item>

            <Form.Item label="Remark" name="remark" labelCol={5}>
              <Input placeholder="Enter remark" />
            </Form.Item>
          </div>

          {/* ── Items ── */}
          <div className="add-amount">
            <ItemTable data1={datafun} total_bam={total_bam} />

            <Form.List name="items">
              {(fields, { remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space
                      key={key}
                      style={{
                        display: "flex",
                        marginBottom: "50px",
                        padding: "20px",
                        flexDirection: "column",
                        background: "#e5e5e5",
                        width: "100%",
                        boxShadow: "0px 0px 2px gray inset",
                      }}
                      align="baseline"
                      className="quotation-form-list"
                    >
                      <Form.Item
                        {...restField}
                        name={[name, "id"]}
                        style={{ display: "none" }}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, "item_id"]}
                        style={{ display: "none" }}
                      >
                        <Input />
                      </Form.Item>

                      {/* Row 1: name, specs, category, rate, unit */}
                      <Row className="from-row">
                        <Form.Item
                          {...restField}
                          name={[name, "item_name"]}
                          rules={[
                            { required: true, message: "Missing PRODUCT NAME" },
                          ]}
                        >
                          <Input
                            placeholder="PRODUCT NAME"
                            className="b c w-cus-moblie"
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "specifications"]}
                        >
                          <Input
                            placeholder="Specifications"
                            className="b c w-cus-moblie"
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "item_category"]}
                          rules={[
                            { required: true, message: "Missing Category" },
                          ]}
                        >
                          <Input
                            placeholder="Enter the Category"
                            className="b c w-cus-moblie"
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "costing"]}
                          rules={[{ required: true, message: "Missing Rate" }]}
                        >
                          <InputNumber
                            className="b w-cus-moblie"
                            placeholder="Rate"
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "unit"]}
                          rules={[{ required: true, message: "Missing unit" }]}
                          style={{ width: "200px" }}
                        >
                          <Select
                            showSearch
                            className="w-cus-moblie"
                            placeholder="Select Unit Type"
                          >
                            {unit.map((u) => (
                              <Select.Option key={u} value={u}>
                                {u}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Row>

                      {/* Row 2: dimensions + quantity + total */}
                      <Row className="from-row">
                        <Form.Item
                          {...restField}
                          label="Length"
                          labelCol={2}
                          name={[name, "length"]}
                        >
                          <InputNumber
                            className="w-cus-moblie"
                            placeholder="Length"
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          label="Height"
                          labelCol={2}
                          name={[name, "height"]}
                        >
                          <InputNumber
                            className="w-cus-moblie"
                            placeholder="Height"
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          label="Depth"
                          labelCol={2}
                          name={[name, "depth"]}
                        >
                          <InputNumber
                            className="w-cus-moblie"
                            placeholder="Depth"
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          label="Width"
                          labelCol={2}
                          name={[name, "width"]}
                        >
                          <InputNumber
                            className="w-cus-moblie"
                            placeholder="Width"
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          label="Quantity"
                          labelCol={2}
                          name={[name, "quantity"]}
                        >
                          <InputNumber
                            className="w-cus-moblie"
                            placeholder="Quantity"
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          label="Total"
                          labelCol={2}
                          name={[name, "total"]}
                          rules={[
                            { required: true, message: "Missing Total" },
                          ]}
                        >
                          <InputNumber
                            className="w-cus-moblie"
                            placeholder="Total"
                          />
                        </Form.Item>
                      </Row>

                      <Button
                        style={{ background: "var(--pr-color)", color: "white" }}
                        className="c"
                        onClick={() => remove(name)}
                      >
                        Delete
                      </Button>
                    </Space>
                  ))}
                </>
              )}
            </Form.List>

            {/* ── Terms & Conditions ── */}
            <div
              className="address"
              style={{ marginTop: "20px", marginBottom: "20px" }}
            >
              <h3 className="h3-title">Terms &amp; Conditions</h3>
              <div style={{ marginBottom: "50px" }}>
                <ReactQuill
                  theme="snow"
                  value={termsValue}
                  onChange={setTerms}
                  style={{ minHeight: "120px" }}
                />
              </div>
            </div>
          </div>

          <p>
            <b>Total Value : </b> {total}
          </p>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={createInfo.isLoading || updateInfo.isLoading}
              style={{ background: "var(--pr-color)" }}
            >
              {id ? "Update" : "Submit"}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default QuotationTemplateForm;
